import { keys } from "@/lib/i18n/portal";
import type {
  SimulatorCatalog,
  SimulatorCategory,
  SimulatorInput,
  SimulatorScenario,
  SimulatorCase,
} from "../types/simulator.types";
export type SimulatorSelection = {
  category: SimulatorCategory;
  provider: string;
  scenario: string;
  case: string;
};
export const initialSelection: SimulatorSelection = {
  category: "payment",
  provider: "",
  scenario: "",
  case: "",
};
/** The server's supported catalog is the only source of selectable combinations. */
export function resolveSelection(
  catalog: SimulatorCatalog | undefined,
  selection: SimulatorSelection,
): SimulatorInput | undefined {
  const providers =
    catalog?.providers.filter(
      (provider) => provider.category === selection.category,
    ) ?? [];
  const provider =
    providers.find((provider) => provider.provider === selection.provider) ??
    providers[0];
  const scenario =
    provider?.scenarios.find(
      (scenario) => scenario.scenario === selection.scenario,
    ) ?? provider?.scenarios[0];
  const caseValue = scenario?.cases.includes(selection.case as SimulatorCase)
    ? (selection.case as SimulatorCase)
    : scenario?.cases.includes("valid")
      ? "valid"
      : scenario?.cases[0];
  return provider && scenario && caseValue
    ? {
        provider: provider.provider,
        scenario: scenario.scenario as SimulatorScenario,
        case: caseValue,
      }
    : undefined;
}
export function selectionKey(input: SimulatorInput | undefined) {
  return input
    ? JSON.stringify({
        provider: input.provider,
        scenario: input.scenario,
        case: input.case,
      })
    : "";
}
/** Technical views mask even synthetic signing material; no HTML or executable content. */
export function sanitizedJson(value: unknown) {
  return (
    JSON.stringify(
      value,
      (key, item) =>
        /(?:signature|hmac|webhookkey|apiaccount|apikey|authorization|cookie|password|secret|token)/i.test(
          key.replace(/[-_]/g, ""),
        )
          ? "[REDACTED]"
          : item,
      2,
    ) ?? "null"
  );
}
export function snapshotRows(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  prefix = "",
): Array<{ field: string; before: unknown; after: unknown }> {
  return Array.from(
    new Set([...Object.keys(before), ...Object.keys(after)]),
  ).flatMap((key) => {
    const left = before[key];
    const right = after[key];
    const field = prefix ? `${prefix}.${key}` : key;
    if (
      (left && typeof left === "object" && !Array.isArray(left)) ||
      (right && typeof right === "object" && !Array.isArray(right))
    ) {
      return snapshotRows(
        (left ?? {}) as Record<string, unknown>,
        (right ?? {}) as Record<string, unknown>,
        field,
      );
    }
    return [{ field, before: left, after: right }];
  });
}

export function statusLabel(value: unknown, t: (key: string) => string) {
  const key = typeof value === "string" ? (keys.integrationSimulator.real.statuses as Record<string, string>)[value.toLowerCase()] : undefined;
  return t(key ?? keys.integrationSimulator.noValue);
}
